/**
 * 🚀 SPACE DASHBOARD - Composant Dashboard Principal
 * Vue d'ensemble du vaisseau - Cockpit principal
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Award, TrendingUp, Truck, MessageSquare, 
  Heart, Gamepad2, Shield, Zap, Activity,
  AlertTriangle, CheckCircle, Clock
} from 'lucide-react';
import { spaceClasses } from './SpaceTheme';

// Composant StatCard holographique
export const HoloStatCard = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle,
  trend,
  color = 'cyan',
  onClick,
  pulse = false
}) => {
  const colorMap = {
    cyan: { border: 'border-cyan-500/30', glow: 'shadow-cyan-500/20', text: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    orange: { border: 'border-orange-500/30', glow: 'shadow-orange-500/20', text: 'text-orange-400', bg: 'bg-orange-500/10' },
    green: { border: 'border-green-500/30', glow: 'shadow-green-500/20', text: 'text-green-400', bg: 'bg-green-500/10' },
    purple: { border: 'border-purple-500/30', glow: 'shadow-purple-500/20', text: 'text-purple-400', bg: 'bg-purple-500/10' },
    pink: { border: 'border-pink-500/30', glow: 'shadow-pink-500/20', text: 'text-pink-400', bg: 'bg-pink-500/10' },
    red: { border: 'border-red-500/30', glow: 'shadow-red-500/20', text: 'text-red-400', bg: 'bg-red-500/10' },
    yellow: { border: 'border-yellow-500/30', glow: 'shadow-yellow-500/20', text: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  };
  
  const colors = colorMap[color] || colorMap.cyan;

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative overflow-hidden cursor-pointer
        backdrop-blur-xl rounded-2xl p-5 
        border ${colors.border} ${colors.bg}
        shadow-lg ${colors.glow}
        transition-all duration-300
        ${pulse ? 'animate-pulse' : ''}
      `}
    >
      {/* Effet de scan */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute w-full h-1 ${colors.text} opacity-20 blur-sm`}
          style={{
            animation: 'scanLine 3s linear infinite',
            top: '0%'
          }}
        />
      </div>

      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{title}</p>
          <p className={`text-3xl font-black ${colors.text}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && (
            <p className="text-gray-600 text-xs mt-1">{subtitle}</p>
          )}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span>{trend >= 0 ? '+' : ''}{trend}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.bg} ${colors.border} border`}>
          <Icon className={`w-6 h-6 ${colors.text}`} />
        </div>
      </div>
    </motion.div>
  );
};

// Composant AlertCard
export const AlertCard = ({ type = 'info', title, message, action, onAction }) => {
  const types = {
    info: { icon: Activity, color: 'cyan', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
    warning: { icon: AlertTriangle, color: 'yellow', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    success: { icon: CheckCircle, color: 'green', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    error: { icon: AlertTriangle, color: 'red', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  };
  
  const config = types[type] || types.info;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex items-center gap-4 p-4 rounded-xl ${config.bg} border ${config.border}`}
    >
      <Icon className={`w-5 h-5 text-${config.color}-400 flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">{title}</p>
        <p className="text-gray-500 text-xs truncate">{message}</p>
      </div>
      {action && (
        <button
          onClick={onAction}
          className={`px-3 py-1 rounded-lg text-xs font-bold bg-${config.color}-500/20 text-${config.color}-400 hover:bg-${config.color}-500/30 transition-colors`}
        >
          {action}
        </button>
      )}
    </motion.div>
  );
};

// Composant QuickAction
export const QuickAction = ({ icon: Icon, label, color = 'cyan', onClick, badge }) => {
  const colorMap = {
    cyan: 'from-cyan-500 to-blue-600 shadow-cyan-500/30',
    orange: 'from-orange-500 to-amber-600 shadow-orange-500/30',
    green: 'from-green-500 to-emerald-600 shadow-green-500/30',
    purple: 'from-purple-500 to-pink-600 shadow-purple-500/30',
    red: 'from-red-500 to-rose-600 shadow-red-500/30',
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative flex flex-col items-center gap-2 p-4 rounded-xl
        bg-gradient-to-br ${colorMap[color]}
        shadow-lg text-white font-medium text-sm
        transition-all hover:shadow-xl
      `}
    >
      {badge > 0 && (
        <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <Icon className="w-6 h-6" />
      <span className="text-xs">{label}</span>
    </motion.button>
  );
};

// Dashboard Principal
const SpaceDashboard = ({ 
  stats = {}, 
  heartbeatsStats = {},
  alerts = [],
  onNavigate,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyan-400 animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
          <span className="text-sm">Chargement des données...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête du cockpit */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
            🚀 COCKPIT PRINCIPAL
          </h1>
          <p className="text-gray-500 text-sm">Vue d'ensemble de La Petite Épicerie</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-400 text-xs font-medium">Systèmes OK</span>
          </div>
        </div>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <HoloStatCard
          icon={Users}
          title="Clients"
          value={stats.totalCustomers || 0}
          subtitle="Inscrits au programme"
          color="cyan"
          onClick={() => onNavigate?.('customers')}
        />
        <HoloStatCard
          icon={Award}
          title="Cartes Complètes"
          value={stats.totalCompletedCards || 0}
          subtitle="Récompenses gagnées"
          color="yellow"
        />
        <HoloStatCard
          icon={TrendingUp}
          title="Tampons"
          value={(stats.alcoholSold || 0) + (stats.cbdSold || 0) + (stats.puffs20kSold || 0) + (stats.puffs28kSold || 0) + (stats.puffs32kSold || 0) + (stats.puffs36kSold || 0)}
          subtitle="Total validés"
          color="green"
        />
        <HoloStatCard
          icon={Heart}
          title="Heartbeats"
          value={heartbeatsStats.totalProfiles || 0}
          subtitle="Profils actifs"
          color="pink"
          onClick={() => onNavigate?.('heartbeats')}
        />
        <HoloStatCard
          icon={Gamepad2}
          title="Joueurs"
          value={heartbeatsStats.gamePlayers || 0}
          subtitle="Participants aux jeux"
          color="purple"
        />
      </div>

      {/* Actions rapides */}
      <div className="backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/20 bg-[#12121a]/60">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          Actions Rapides
        </h3>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          <QuickAction icon={Truck} label="Livraisons" color="orange" onClick={() => onNavigate?.('delivery')} badge={stats.pendingDeliveries || 0} />
          <QuickAction icon={MessageSquare} label="Messages" color="cyan" onClick={() => onNavigate?.('messaging')} badge={stats.unreadMessages || 0} />
          <QuickAction icon={Users} label="Clients" color="green" onClick={() => onNavigate?.('customers')} />
          <QuickAction icon={Heart} label="Heartbeats" color="purple" onClick={() => onNavigate?.('heartbeats')} />
          <QuickAction icon={Shield} label="Équipe" color="orange" onClick={() => onNavigate?.('vendors')} />
          <QuickAction icon={Award} label="Promos" color="yellow" onClick={() => onNavigate?.('promos')} />
          <QuickAction icon={Activity} label="Analytics" color="cyan" onClick={() => onNavigate?.('analytics')} />
          <QuickAction icon={Gamepad2} label="Jeux" color="pink" onClick={() => onNavigate?.('games')} />
        </div>
      </div>

      {/* Alertes et notifications */}
      {alerts.length > 0 && (
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-orange-500/20 bg-[#12121a]/60">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            Alertes ({alerts.length})
          </h3>
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert, idx) => (
              <AlertCard key={idx} {...alert} />
            ))}
          </div>
        </div>
      )}

      {/* Activité récente - Mini timeline */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Dernières ventes */}
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/20 bg-[#12121a]/60">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-cyan-400" />
            Activité Récente
          </h3>
          <div className="space-y-3 text-sm">
            {stats.recentActivity?.length > 0 ? (
              stats.recentActivity.slice(0, 5).map((activity, idx) => (
                <div key={idx} className="flex items-center gap-3 text-gray-400">
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                  <span className="flex-1">{activity.message}</span>
                  <span className="text-xs text-gray-600">{activity.time}</span>
                </div>
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">Aucune activité récente</p>
            )}
          </div>
        </div>

        {/* Stats détaillées ventes */}
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-green-500/20 bg-[#12121a]/60">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Détail des Ventes
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-2xl font-bold text-red-400">{stats.alcoholSold || 0}</p>
              <p className="text-xs text-gray-500">Alcool</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-green-500/10 border border-green-500/20">
              <p className="text-2xl font-bold text-green-400">{stats.cbdSold || 0}</p>
              <p className="text-xs text-gray-500">CBD</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
              <p className="text-2xl font-bold text-purple-400">
                {(stats.puffs20kSold || 0) + (stats.puffs28kSold || 0) + (stats.puffs32kSold || 0) + (stats.puffs36kSold || 0)}
              </p>
              <p className="text-xs text-gray-500">Puffs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceDashboard;
